import { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from "@/components/ui/toaster";
import { ThemeProvider } from '@/components/theme-provider';
import { ThemeProvider as EnhancedThemeProvider, defaultTheme } from '@/contexts/ThemeProvider';
import { SettingsProvider } from '@/components/settings/lib/settings-context';
import { useSettings } from '@/components/settings/lib/settings-context';
import { Navigate } from 'react-router-dom';
import { ClientProvider } from '@/context/client-context';
import { ClientsCacheLayout } from '@/components/clients/ClientsCacheLayout';
import { UserInfoProvider } from '@/context/user-info-context';
import { PlanProvider } from '@/context/plan-context';
import { AuthProvider } from '@/contexts/AuthContext';
import { AdminProvider } from '@/contexts/AdminContext';
import { AbilityProvider } from '@/components/authnz/AbilityContext';
import { ProtectedRoute } from '@/components/authnz/ProtectedRoute';
import { RoleBasedRoute } from '@/components/authnz/RoleBasedRoute';
import { resetTaxonomyToDefaults, clearAllTaxonomyCache, loadTaxonomyFromFile, getLastMergedVersion } from '@/lib/taxonomy-loader';
import { DiagnosticLogMonitor } from '@/components/diagnostic-log-monitor';
import { PostureModeProvider } from '@/components/posture-analysis/lib/PostureModeContext';
import { SELF_ASSESSMENT_STRATEGY } from '@/components/posture-analysis/lib/posture-mode-strategy';

// Import pages
import LoginPage from './pages/LoginPage';
import { RootRedirect } from './pages/RootRedirect';
import { WelcomePage } from './pages/WelcomePage';
import PurchasePage from './pages/PurchasePage';
import PurchaseSuccessPage from './pages/PurchaseSuccessPage';
import Dashboard from './components/dashboard/Dashboard';
import ChatPage from './pages/ChatPage';
import AgentPage from './components/agent/AgentPage';
import CoachingPage from './pages/CoachingPage';
import ProgressionModelPage from './pages/ProgressionModelPage';
import SettingsPage from './components/settings/SettingsPage';
import DrillPlannerPage from './components/drill-planner/DrillPlannerPage';
import HarryUIDemo from './pages/HarryUIDemo';
import SelfAssessmentSimplePage from './pages/SelfAssessmentSimplePage';
import { AdminProspectsPage } from './pages/admin/AdminProspectsPage';
import { SignUpLandingPage } from './components/authnz/SignUpLandingPage';
import { AdminUsersPage } from './components/authnz/AdminUsersPage';
import { AdminStatisticsPage } from './components/admin/AdminStatisticsPage';
import { FunnelDropOffAnalyticsPage } from './pages/admin/FunnelDropOffAnalyticsPage';
import SubscriptionsPage from './components/subscriptions/SubscriptionsPage';
import HelpPage from './components/help/HelpPage';

// Import client pages
import ClientsPage from './components/clients/ClientsPage';
import ClientDetailPage from './components/clients/ClientDetailPage';
import ClientEditPage from './components/clients/ClientEditPage';
import NewClientPage from './components/clients/NewClientPage';

import LegacySopAssessmentPage from './components/sop-intake/SopAssessmentPage';
import RebuiltSopAssessmentPage from './components/sop-intake/sop-analyzer/SopAssessmentPage';
import SopViewPage from './components/sop-intake/sop-analyzer/SopViewPage';
import SopEditPage from './components/sop-intake/sop-analyzer/SopEditPage';
import SopNewPage from './components/sop-intake/sop-analyzer/SopNewPage';

// Import posture analysis pages
import ClientPostureAnalysisPage from './components/posture-analysis/ClientPostureAnalysisPage';
import PostureAnalysisPage from './components/posture-analysis/PostureAnalysisPage';
import RemoteAssessmentPage from './components/remote-assessment/RemoteAssessmentPage';

// Import drill library page
import DrillsPage from './components/drill-library/DrillsPage';
import DrillPlanLibraryPage from './components/drill-planner/DrillPlanLibraryPage';

// Extend Window interface for debug utilities
declare global {
  interface Window {
    __harryDebug?: {
      resetTaxonomy: () => Promise<void>;
      clearTaxonomyCache: () => void;
    };
  }
}
import logger from '@/lib/logging';
import {
  SELF_ASSESSMENT_LEGACY_CAPTURE_PATH,
  SELF_ASSESSMENT_LEGACY_SIMPLE_PATH,
  SELF_ASSESSMENT_PUBLIC_PATH,
} from '@/lib/self-assessment-public-path';
import * as React from 'react';

function RedirectLegacySelfAssessmentUrl() {
  const { search } = useLocation();
  return <Navigate to={`${SELF_ASSESSMENT_PUBLIC_PATH}${search}`} replace />;
}

function App() {
  // Check taxonomy version on app startup to detect updates
  useEffect(() => {
    const checkTaxonomyVersion = async () => {
      try {
        const currentJson = await loadTaxonomyFromFile();
        const lastMerged = getLastMergedVersion();

        // If version changed since last merge, clear cache to force refresh
        if (lastMerged && lastMerged !== currentJson.version) {
          logger.debug(`🔄 Taxonomy version changed (${lastMerged} → ${currentJson.version}), clearing cache...`);
          clearAllTaxonomyCache();
          logger.debug('✅ Cache cleared. Taxonomy will reload with new version on next access.');
        }
      } catch (error) {
        // Silently fail - don't block app startup if taxonomy check fails
        logger.warn('Failed to check taxonomy version on startup:', error);
      }
    };

    checkTaxonomyVersion();
  }, []);

  // Expose debug utilities to window for console access
  useEffect(() => {
    window.__harryDebug = {
      resetTaxonomy: async () => {
        logger.debug('🔄 Resetting taxonomy to factory defaults...');
        await resetTaxonomyToDefaults();
        logger.debug('✅ Taxonomy reset complete. Refresh the page to see changes.');
        window.location.reload();
      },
      clearTaxonomyCache: () => {
        logger.debug('🗑️ Clearing taxonomy cache...');
        clearAllTaxonomyCache();
        logger.debug('✅ Cache cleared. Taxonomy will reload from source on next access.');
      }
    };
    logger.debug('💡 Debug utilities available: window.__harryDebug.resetTaxonomy(), window.__harryDebug.clearTaxonomyCache()');

    return () => {
      delete window.__harryDebug;
    };
  }, []);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem
      themes={['light', 'dark', 'high-contrast', 'glass']}
    >
      <EnhancedThemeProvider initialTheme={defaultTheme}>
        <Routes>
          {/* ── PUBLIC: Self-Assessment — minimal providers, no auth ─────── */}
          <Route path={SELF_ASSESSMENT_PUBLIC_PATH} element={
            <PostureModeProvider mode={SELF_ASSESSMENT_STRATEGY}>
              <SelfAssessmentSimplePage />
              <Toaster />
            </PostureModeProvider>
          } />
          <Route path={`${SELF_ASSESSMENT_LEGACY_SIMPLE_PATH}/*`} element={<RedirectLegacySelfAssessmentUrl />} />
          <Route path={SELF_ASSESSMENT_LEGACY_SIMPLE_PATH} element={<RedirectLegacySelfAssessmentUrl />} />
          <Route path={SELF_ASSESSMENT_LEGACY_CAPTURE_PATH} element={<RedirectLegacySelfAssessmentUrl />} />

          {/* ── ALL OTHER ROUTES — full provider stack ──────────────────── */}
          <Route path="/*" element={<AuthenticatedApp />} />
        </Routes>
      </EnhancedThemeProvider>
    </ThemeProvider >
  );
}

/**
 * Full-provider application shell for all authenticated / standard routes.
 * Self-assessment is handled separately above with minimal providers.
 */
function AuthenticatedApp() {
  return (
    <AuthProvider>
      <UserInfoProvider>
        <AbilityProvider>
        <SettingsProvider>
          <AdminProvider>
            <ClientProvider>
                <PlanProvider>
                <Routes>
                  <Route path="/" element={<RootRedirect />} />
                  <Route path="/welcome" element={<WelcomePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/purchase" element={<PurchasePage />} />
                  <Route path="/purchase/success" element={<PurchaseSuccessPage />} />
                  <Route path="/signup-required" element={<SignUpLandingPage />} />

                  {/* Admin User Management - Administrator Only */}
                  <Route path="/admin/users" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="manage" requiredSubject="RoleManagement">
                        <AdminUsersPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  {/* Admin Statistics - Administrator Only */}
                  <Route path="/admin/statistics" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="manage" requiredSubject="RoleManagement">
                        <AdminStatisticsPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/admin/funnel" element={<Navigate to="/sales/funnel" replace />} />
                  <Route path="/sales/funnel" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="read" requiredSubject="FunnelAnalytics">
                        <FunnelDropOffAnalyticsPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  {/* Subscriptions & Products Management */}
                  <Route path="/subscriptions" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="create" requiredSubject="Subscription">
                        <SubscriptionsPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  {/* Dashboard + clients list — route-scoped ClientsCache */}
                  <Route element={<ClientsCacheLayout />}>
                    <Route path="/dashboard" element={
                      <ProtectedRoute>
                        <RoleBasedRoute requiredAction="access" requiredSubject="Dashboard">
                          <Dashboard />
                        </RoleBasedRoute>
                      </ProtectedRoute>
                    } />

                    <Route path="/clients" element={
                      <ProtectedRoute>
                        <RoleBasedRoute requiredAction="read" requiredSubject="Client">
                          <ClientsPage />
                        </RoleBasedRoute>
                      </ProtectedRoute>
                    } />
                  </Route>

                  <Route path="/clients/new" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="create" requiredSubject="Client">
                        <NewClientPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/clients/:id" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="read" requiredSubject="Client">
                        <ClientDetailPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/clients/:id/edit" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="update" requiredSubject="Client">
                        <ClientEditPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/clients/:id/assessment/:assessmentId" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="read" requiredSubject="Assessment">
                        <SopViewPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/clients/:id/assessment/:assessmentId/edit" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="update" requiredSubject="Assessment">
                        <SopEditPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/clients/:id/assessment/new" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="create" requiredSubject="Assessment">
                        <SopNewPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/clients/:id/posture-analysis/:analysisId" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="read" requiredSubject="PostureAnalysis">
                        <ClientPostureAnalysisPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/chat" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="access" requiredSubject="Chat">
                        <ChatPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/agent" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="access" requiredSubject="Agent">
                        <ExperimentalFeatureGate>
                          <AgentPage />
                        </ExperimentalFeatureGate>
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/coaching" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="access" requiredSubject="Coaching">
                        <CoachingPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/drills" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="read" requiredSubject="Drill">
                        <DrillsPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  {/* Legacy alias — Client 360 "Open Drill Library" used this path before /drills */}
                  <Route path="/drill-library" element={
                    <ProtectedRoute>
                      <DrillLibraryRedirect />
                    </ProtectedRoute>
                  } />

                  <Route path="/drill-planner" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="read" requiredSubject="Drill">
                        <ExperimentalFeatureGate>
                          <DrillPlannerPage />
                        </ExperimentalFeatureGate>
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/drill-plan-library" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="read" requiredSubject="DrillPlan">
                        <DrillPlanLibraryPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/posture-analysis" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="access" requiredSubject="PostureAnalysis">
                        <PostureAnalysisPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/remote-assessment" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="access" requiredSubject="PostureAnalysis">
                        <RemoteAssessmentPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/progression-model" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="access" requiredSubject="ProgressionModel">
                        <ProgressionModelPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  {/* No RoleBasedRoute: a session without app access (never invited,
                      or lapsed) still needs Settings to see billing state and buy/renew
                      a plan (#7782). Admin-only cards gate themselves individually. */}
                  <Route path="/settings" element={
                    <ProtectedRoute>
                      <SettingsPage />
                    </ProtectedRoute>
                  } />

                  {/* Help (video-first, ADR-0025): any signed-in user */}
                  <Route path="/help" element={
                    <ProtectedRoute>
                      <HelpPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/help/:subsection" element={
                    <ProtectedRoute>
                      <HelpPage />
                    </ProtectedRoute>
                  } />

                  <Route path="/sop-assessment" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="read" requiredSubject="Assessment">
                        <RebuiltSopAssessmentPage mode="new" />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/sop-assessment-legacy" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="read" requiredSubject="Assessment">
                        <LegacySopAssessmentPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />

                  <Route path="/harry-ui-demo" element={<HarryUIDemo />} />

                  <Route path="/admin/prospects" element={<Navigate to="/sales/prospects" replace />} />
                  <Route path="/admin/prospects/:id" element={<Navigate to="/sales/prospects" replace />} />

                  <Route path="/sales/prospects" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="read" requiredSubject="ClientProspect">
                        <AdminProspectsPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />
                  <Route path="/sales/prospects/:id" element={
                    <ProtectedRoute>
                      <RoleBasedRoute requiredAction="read" requiredSubject="ClientProspect">
                        <AdminProspectsPage />
                      </RoleBasedRoute>
                    </ProtectedRoute>
                  } />
                </Routes>
                <Toaster />
                <DiagnosticLogMonitor />
              </PlanProvider>
            </ClientProvider>
          </AdminProvider>
        </SettingsProvider>
      </AbilityProvider>
      </UserInfoProvider>
    </AuthProvider>
  );
}

// Simple gate to hide experimental pages unless enabled
function ExperimentalFeatureGate({ children }: { children: React.ReactElement }) {
  const { settings, isLoading } = useSettings();

  if (isLoading) {
    return <div className="flex h-[50vh] items-center justify-center text-muted-foreground">Loading...</div>;
  }

  if (!settings.experimentalFeaturesEnabled) {
    return <Navigate to="/drills" replace />;
  }
  return children;
}

/** Preserve router state when redirecting legacy /drill-library bookmarks to /drills. */
function DrillLibraryRedirect() {
  const location = useLocation();
  return <Navigate to="/drills" replace state={location.state} />;
}

export default App;
